import {
  Auth,
  GoogleAuthProvider,
  User,
  authState,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from '@angular/fire/auth';

import { Injectable, inject } from '@angular/core';
import { LoginData } from '../../interfaces/login-data.interface';
import { Observable, tap } from 'rxjs';
import { CapicheLoggerService } from 'capiche-logger';

import { CapicheAuthCore, AuthUser } from '@capiche/ts-auth-core';
import { localStorageAdapter } from './localStorageAdapter';
import { CAPICHE_AUTH_CONFIG, SchoolSearchResult, StudentLoginData } from '../../capiche-auth.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  config = inject(CAPICHE_AUTH_CONFIG);
  private logService: boolean = true; // enable logging for the service
  private logCore: boolean = false; // enable logging for the core

  authState$: Observable<User | null>;
  logger = new CapicheLoggerService('auth.service:', this.logService);
  private core: CapicheAuthCore;

  constructor(private auth: Auth) {
    this.logger.log('constructor', this.config.envName, this.config.googleSignInHandler);

    this.core = new CapicheAuthCore(
      localStorageAdapter,
      {
        onLogin: (user) => this.logger.log('AuthCore login', user.uid),
        onLogout: () => this.logger.log('AuthCore logout'),
      },
      this.logCore,
    );

    this.authState$ = authState(this.auth).pipe(
      tap(async (user) => {
        if (!user) return;
        const idToken = await user.getIdToken();
        await this.core.login(idToken); // keep core in sync
      }),
    );

    this.core.init(); // restore from storage
  }

  async login({ email, password }: LoginData) {
    const result = await signInWithEmailAndPassword(this.auth, email, password);
    const idToken = await result.user.getIdToken();
    await this.core.login(idToken);
    return result;
  }

  async loginWithGoogle() {
    this.logger.log('loginWithGoogle', this.config.envName, this.config.googleSignInHandler);
    // Use the injected googleSignInHandler from CAPICHE_AUTH_CONFIG
    if (this.config.googleSignInHandler) {
      this.logger.log('loginWithGoogle: using googleSignInHandler');
      // Call the handler, which could either be a popup for web apps or background messaging for Chrome extension
      await this.config.googleSignInHandler(); // This invokes the appropriate sign-in flow based on the environment
    } else {
      this.logger.log('loginWithGoogle: using default sign-in flow');
      // Fallback: if googleSignInHandler is not available, use the default sign-in flow (web app)
      const result = await signInWithPopup(this.auth, new GoogleAuthProvider());
      const idToken = await result.user.getIdToken();
      await this.core.login(idToken);
    }
  }
  async register(data: LoginData) {
    if (this.config.registerHandler) {
      return this.config.registerHandler(data);
    }
    const result = await createUserWithEmailAndPassword(this.auth, data.email, data.password);
    const idToken = await result.user.getIdToken();
    await this.core.login(idToken);

    // CSW-1549: credit the referrer for this signup, if a ?ref= code was
    // present. Additive — only runs on this default (non-custom-handler)
    // path, so apps with their own registerHandler (e.g. teacher-portal,
    // which already resolves referrals server-side) are unaffected. Must
    // never block/fail registration — the account already exists by now.
    if (data.refCode && this.config.resolveReferralHandler) {
      try {
        await this.config.resolveReferralHandler(data.refCode, result.user.uid);
      } catch (err) {
        this.logger.error('register: resolveReferralHandler failed', err);
      }
    }

    return result;
  }

  forgotPassword(email: string): Promise<void> {
    if (this.config.forgotPasswordHandler) {
      return this.config.forgotPasswordHandler(email);
    }
    return sendPasswordResetEmail(this.auth, email);
  }

  /** CSW-1379 follow-up: dynamic student path on the shared login form. */
  get studentLoginAvailable(): boolean {
    return !!this.config.schoolSearchHandler && !!this.config.studentLoginHandler;
  }

  async searchSchools(query: string): Promise<SchoolSearchResult[]> {
    if (!this.config.schoolSearchHandler) return [];
    return this.config.schoolSearchHandler(query);
  }

  async studentLogin(data: StudentLoginData): Promise<void> {
    if (!this.config.studentLoginHandler) {
      throw new Error('studentLoginHandler not configured');
    }
    return this.config.studentLoginHandler(data);
  }

  async logout() {
    await this.core.logout();
    return signOut(this.auth);
  }

  getUser(): AuthUser | null {
    return this.core.getUser();
  }

  getIdToken(): string | null {
    return this.core.getIdToken();
  }
}
